import { computed, ComputedRef } from 'vue';
import { Api } from '@/api/api.client';
import { useReferralStore } from '@/stores/referral.store';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { ErrorTypes } from '@/api/generated/api.enum';
import { getErrorType } from '@/utils/error.utils';
import { Components } from '@/api/generated/api';

interface IUseReferralAttributionComposable {
  code: ComputedRef<string | null>;
  hasCode: ComputedRef<boolean>;
  captureFromLink: (code: string) => void;
  attributeIfPossible: (source?: 'signup' | 'checkout') => Promise<void>;
  clear: () => void;
}

const expectedReferralErrors = new Set<Components.Schemas.ErrorTypes>([
  ErrorTypes.INVALID_REFERRAL_CODE,
  ErrorTypes.REFERRAL_ALREADY_ATTRIBUTED,
  ErrorTypes.REFERRAL_SELF_REFERRAL,
  ErrorTypes.REFERRAL_NOT_ELIGIBLE,
]);

export const useReferralAttribution = (): IUseReferralAttributionComposable => {
  const store = useReferralStore();
  const { trackEvent } = useAnalytics();

  const code = computed(() => store.code);
  const hasCode = computed(() => store.hasCode);

  const captureFromLink = (rawCode: string): void => {
    const before = store.code;
    store.setCode(rawCode);

    if (store.code && store.code !== before) {
      trackEvent(AnalyticsEvents.referral.linkOpened);
    }
  };

  const clear = (): void => {
    store.clear();
  };

  const attributeIfPossible = async (source: 'signup' | 'checkout' = 'signup'): Promise<void> => {
    if (!store.code) return;

    try {
      await Api.Routes.Referral.attributeReferral(null, {
        code: store.code!,
        source,
      });

      trackEvent(AnalyticsEvents.referral.attributed);
      store.clear();
    } catch (e) {
      const errorType = getErrorType(e);

      if (errorType && expectedReferralErrors.has(errorType)) {
        store.clear();
        return;
      }

      console.error('Unexpected referral attribution error', e);
    }
  };

  return {
    code,
    hasCode,
    captureFromLink,
    attributeIfPossible,
    clear,
  };
};
